/**
 * Hues.js Website — Main Application
 * -----------------------------------------------------------------------------
 * A real website built entirely with the published @deathlegion/hues framework.
 * Features:
 *   • Multi-page navigation via @hues/router (hash mode)
 *   • Reactive state via @hues/store
 *   • Live interactive demos (counter, todo, chart)
 *   • Code samples with copy-to-clipboard
 *   • Installation guide
 *   • Feature showcase
 *
 * This file is bundled with esbuild and loaded as an ES module in index.html.
 *
 * © Death Legion — Hues.js
 */

import {
  createApp,
  defineComponent,
  h,
  ref,
  reactive,
  computed,
  Fragment,
  Text,
  onMounted,
  onUnmounted,
  HUES_VERSION,
  HUES_AUTHOR,
} from '@deathlegion/hues'
import { createRouter, useRouter, RouterView, RouterLink } from '@deathlegion/hues-router'
import { createStore, useStore } from '@deathlegion/hues-store'

// ───────────────────────── Logo ─────────────────────────

function Logo({ size = 32 }) {
  return h('svg', {
    width: size, height: size, viewBox: '0 0 48 48',
    class: 'brand__logo',
  }, [
    h('defs', null, [
      h('linearGradient', { id: 'logoGradient', x1: '0', y1: '0', x2: '48', y2: '48', gradientUnits: 'userSpaceOnUse' }, [
        h('stop', { offset: '0%', 'stop-color': '#42b883' }),
        h('stop', { offset: '100%', 'stop-color': '#35495e' }),
      ]),
    ]),
    h('rect', { x: 2, y: 2, width: 44, height: 44, rx: 10, fill: 'url(#logoGradient)' }),
    h('path', { d: 'M16 14 L16 34 M16 24 L32 24 M32 14 L32 34', stroke: 'white', 'stroke-width': 3.5, 'stroke-linecap': 'round' }),
    h('circle', { cx: 24, cy: 24, r: 3, fill: 'white' }),
  ])
}

// ───────────────────────── Header ─────────────────────────

const Header = defineComponent({
  name: 'SiteHeader',
  setup() {
    const router = useRouter()
    const currentPath = computed(() => router.currentRoute.value.path)
    const navItems = [
      { path: '/', label: 'Home' },
      { path: '/demos', label: 'Demos' },
      { path: '/features', label: 'Features' },
      { path: '/install', label: 'Install' },
      { path: '/docs', label: 'Docs' },
    ]
    return () => h('header', { class: 'header' }, [
      h('div', { class: 'header__inner' }, [
        h('a', {
          class: 'brand',
          href: '#/',
          onClick: (e) => { e.preventDefault(); router.push('/') },
        }, [
          h(Logo, { size: 32 }),
          h('div', null, [
            h('div', { class: 'brand__name' }, ['Hues', h('span', null, '.js')]),
            h('div', { class: 'brand__tag' }, 'by Death Legion'),
          ]),
        ]),
        h('nav', { class: 'nav' },
          navItems.map((item) =>
            h('button', {
              key: item.path,
              class: `nav__link ${currentPath.value === item.path ? 'nav__link--active' : ''}`,
              onClick: () => router.push(item.path),
            }, item.label),
          ),
        ),
        h('div', { class: 'header__actions' }, [
          h('span', { class: 'badge' }, `v${HUES_VERSION}`),
        ]),
      ]),
    ])
  },
})

// ───────────────────────── Hero ─────────────────────────

const Hero = defineComponent({
  name: 'Hero',
  setup() {
    const router = useRouter()
    const stats = [
      { value: '11', label: 'Reactivity primitives' },
      { value: '13', label: 'Built-in directives' },
      { value: '5', label: 'Ecosystem packages' },
      { value: '~12kb', label: 'Min+gzip' },
    ]
    return () => h('section', { class: 'hero' }, [
      h('div', { class: 'hero__inner' }, [
        h('div', { class: 'hero__badge' }, [
          h('span', { class: 'hero__pulse' }),
          `Progressive JavaScript Framework · v${HUES_VERSION}`,
        ]),
        h('h1', { class: 'hero__title' }, [
          'The ', h('span', { class: 'gradient' }, 'Hues.js'), ' Framework',
        ]),
        h('p', { class: 'hero__subtitle' }, [
          'An incrementally-adoptable framework for building user interfaces — reactivity, components, router, store, SSR, and CLI. Built in TypeScript by ', h('strong', null, 'Death Legion'), '.',
        ]),
        h('div', { class: 'hero__actions' }, [
          h('button', {
            class: 'btn btn--primary btn--lg',
            onClick: () => router.push('/demos'),
          }, ['Try Live Demos ', h('span', null, '→')]),
          h('button', {
            class: 'btn btn--ghost btn--lg',
            onClick: () => router.push('/install'),
          }, 'Installation'),
        ]),
        h('div', { class: 'hero__stats' },
          stats.map((s) =>
            h('div', { class: 'hero__stat', key: s.label }, [
              h('strong', null, s.value), ' · ', s.label,
            ]),
          ),
        ),
      ]),
    ])
  },
})

// ───────────────────────── Counter Demo ─────────────────────────

const CounterDemo = defineComponent({
  name: 'CounterDemo',
  setup() {
    const count = ref(0)
    const doubled = computed(() => count.value * 2)
    const isEven = computed(() => count.value % 2 === 0)
    return () => h('div', { class: 'counter' }, [
      h('div', { class: 'counter__value' }, [
        h('span', {
          class: 'counter__number',
          style: { color: isEven.value ? '#42b883' : '#ff6b6b' },
        }, String(count.value)),
        h('span', { class: 'counter__label' }, isEven.value ? 'even' : 'odd'),
      ]),
      h('div', { class: 'counter__doubled' }, `Doubled: ${doubled.value}`),
      h('div', { class: 'counter__buttons' }, [
        h('button', { class: 'btn btn--primary', onClick: () => count.value-- }, '−'),
        h('button', { class: 'btn btn--ghost', onClick: () => count.value = 0 }, 'Reset'),
        h('button', { class: 'btn btn--primary', onClick: () => count.value++ }, '+'),
      ]),
    ])
  },
})

// ───────────────────────── Todo Demo ─────────────────────────

interface Todo { id: number; text: string; done: boolean }

const todoStore = createStore<{ todos: Todo[]; filter: 'all' | 'active' | 'completed' }>({
  state() {
    return {
      todos: [
        { id: 1, text: 'Learn Hues.js reactivity', done: true },
        { id: 2, text: 'Build a Todo app', done: false },
        { id: 3, text: 'Ship to production', done: false },
      ],
      filter: 'all',
    }
  },
  getters: {
    remaining: (s) => s.todos.filter((t) => !t.done).length,
    completed: (s) => s.todos.filter((t) => t.done).length,
    filtered: (s) => {
      if (s.filter === 'active') return s.todos.filter((t) => !t.done)
      if (s.filter === 'completed') return s.todos.filter((t) => t.done)
      return s.todos
    },
  },
  mutations: {
    addTodo(s, text: string) {
      if (!text.trim()) return
      s.todos.push({ id: Date.now(), text: text.trim(), done: false })
    },
    toggle(s, id: number) {
      const todo = s.todos.find((t) => t.id === id)
      if (todo) todo.done = !todo.done
    },
    remove(s, id: number) {
      s.todos = s.todos.filter((t) => t.id !== id)
    },
    setFilter(s, filter: 'all' | 'active' | 'completed') {
      s.filter = filter
    },
    clearCompleted(s) {
      s.todos = s.todos.filter((t) => !t.done)
    },
  },
})

const TodoDemo = defineComponent({
  name: 'TodoDemo',
  setup() {
    const store = useStore<typeof todoStore.state extends infer S ? S : any>() as typeof todoStore
    const newText = ref('')
    const addTodo = () => {
      if (!newText.value.trim()) return
      store.commit('addTodo', newText.value)
      newText.value = ''
    }
    return () => {
      const filtered = store.getters.filtered as Todo[]
      const filter = store.state.filter
      const remaining = store.getters.remaining as number
      const completed = store.getters.completed as number
      return h('div', { class: 'todo' }, [
        h('div', { class: 'todo__header' }, [
          h('input', {
            class: 'todo__input',
            type: 'text',
            placeholder: 'What needs to be done?',
            value: newText.value,
            onInput: (e: Event) => { newText.value = (e.target as HTMLInputElement).value },
            onKeydown: (e: KeyboardEvent) => { if (e.key === 'Enter') addTodo() },
          }),
          h('button', { class: 'btn btn--primary', onClick: addTodo }, 'Add'),
        ]),
        h('div', { class: 'todo__filters' }, [
          h('button', {
            class: `chip ${filter === 'all' ? 'chip--active' : ''}`,
            onClick: () => store.commit('setFilter', 'all'),
          }, `All (${store.state.todos.length})`),
          h('button', {
            class: `chip ${filter === 'active' ? 'chip--active' : ''}`,
            onClick: () => store.commit('setFilter', 'active'),
          }, `Active (${remaining})`),
          h('button', {
            class: `chip ${filter === 'completed' ? 'chip--active' : ''}`,
            onClick: () => store.commit('setFilter', 'completed'),
          }, `Completed (${completed})`),
        ]),
        h('ul', { class: 'todo__list' },
          filtered.map((todo) =>
            h('li', {
              key: todo.id,
              class: `todo__item ${todo.done ? 'todo__item--done' : ''}`,
            }, [
              h('label', { class: 'todo__row' }, [
                h('input', {
                  type: 'checkbox',
                  checked: todo.done,
                  onChange: () => store.commit('toggle', todo.id),
                }),
                h('span', { class: 'todo__text' }, todo.text),
              ]),
              h('button', {
                class: 'todo__remove',
                onClick: () => store.commit('remove', todo.id),
              }, '×'),
            ]),
          ),
        ),
        h('div', { class: 'todo__footer' }, [
          h('span', null, `${remaining} item${remaining === 1 ? '' : 's'} left`),
          completed > 0
            ? h('button', {
                class: 'btn btn--ghost btn--sm',
                onClick: () => store.commit('clearCompleted'),
              }, 'Clear completed')
            : h('span', null, ''),
        ]),
      ])
    }
  },
})

// ───────────────────────── Live Chart Demo ─────────────────────────

const ChartDemo = defineComponent({
  name: 'ChartDemo',
  setup() {
    const data = ref<number[]>([42, 28, 65, 39, 78, 55, 91, 47])
    const labels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug']
    const max = computed(() => Math.max(...data.value, 100))
    const avg = computed(() => Math.round(data.value.reduce((a, b) => a + b, 0) / data.value.length))
    let timer: ReturnType<typeof setInterval> | null = null

    const shuffle = () => {
      data.value = data.value.map(() => Math.floor(Math.random() * 100) + 10)
    }
    const startLive = () => {
      if (timer) return
      timer = setInterval(() => {
        data.value = [...data.value.slice(1), Math.floor(Math.random() * 100) + 10]
      }, 800)
    }
    const stopLive = () => {
      if (timer) { clearInterval(timer); timer = null }
    }

    onUnmounted(() => stopLive())

    return () => {
      const width = 360
      const height = 160
      const padding = 24
      const barWidth = (width - padding * 2) / data.value.length - 4
      const maxVal = max.value
      const bars = data.value.map((v, i) => {
        const barHeight = (v / maxVal) * (height - padding * 2)
        const x = padding + i * (barWidth + 4)
        const y = height - padding - barHeight
        return h('g', { key: i }, [
          h('rect', { x, y, width: barWidth, height: barHeight, fill: '#42b883', rx: 2 }),
          h('text', {
            x: x + barWidth / 2, y: y - 3,
            'text-anchor': 'middle', 'font-size': 9, fill: '#94a3b8',
          }, String(v)),
          h('text', {
            x: x + barWidth / 2, y: height - padding + 12,
            'text-anchor': 'middle', 'font-size': 9, fill: '#64748b',
          }, labels[i]),
        ])
      })
      return h('div', null, [
        h('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem', marginBottom: '0.75rem' } }, [
          h('div', { style: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '0.5rem', padding: '0.5rem', textAlign: 'center' } }, [
            h('div', { style: { fontSize: '1.25rem', fontWeight: 700, color: 'var(--green)', fontVariantNumeric: 'tabular-nums' } }, String(maxVal)),
            h('div', { style: { fontSize: '0.625rem', color: 'var(--text-muted)', textTransform: 'uppercase' } }, 'Max'),
          ]),
          h('div', { style: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '0.5rem', padding: '0.5rem', textAlign: 'center' } }, [
            h('div', { style: { fontSize: '1.25rem', fontWeight: 700, color: 'var(--green)', fontVariantNumeric: 'tabular-nums' } }, String(avg.value)),
            h('div', { style: { fontSize: '0.625rem', color: 'var(--text-muted)', textTransform: 'uppercase' } }, 'Avg'),
          ]),
          h('div', { style: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '0.5rem', padding: '0.5rem', textAlign: 'center' } }, [
            h('div', { style: { fontSize: '1.25rem', fontWeight: 700, color: 'var(--green)', fontVariantNumeric: 'tabular-nums' } }, String(data.value.reduce((a, b) => a + b, 0))),
            h('div', { style: { fontSize: '0.625rem', color: 'var(--text-muted)', textTransform: 'uppercase' } }, 'Total'),
          ]),
        ]),
        h('svg', { viewBox: `0 0 ${width} ${height}`, style: { width: '100%', height: 'auto', background: 'var(--surface)', borderRadius: '0.5rem', border: '1px solid var(--border)' } }, bars),
        h('div', { style: { display: 'flex', gap: '0.375rem', justifyContent: 'center', marginTop: '0.75rem' } }, [
          h('button', { class: 'btn btn--primary btn--sm', onClick: shuffle }, 'Shuffle'),
          h('button', { class: 'btn btn--ghost btn--sm', onClick: startLive }, '▶ Live'),
          h('button', { class: 'btn btn--ghost btn--sm', onClick: stopLive }, '■ Stop'),
        ]),
      ])
    }
  },
})

// ───────────────────────── Demo Card wrapper ─────────────────────────

const DemoCard = defineComponent({
  name: 'DemoCard',
  props: {
    title: { type: String, required: true },
    subtitle: { type: String, default: '' },
    desc: { type: String, default: '' },
  },
  setup(props, ctx) {
    return () => h('div', { class: 'demo-card' }, [
      h('div', { class: 'demo-card__header' }, [
        h('div', { class: 'demo-card__title' }, props.title),
        h('div', { class: 'demo-card__subtitle' }, props.subtitle),
        props.desc ? h('div', { class: 'demo-card__desc' }, props.desc) : h(Fragment, null, []),
      ]),
      h('div', { class: 'demo-card__body' }, ctx.slots.default ? ctx.slots.default() : []),
    ])
  },
})

// ───────────────────────── Pages ─────────────────────────

const HomePage = defineComponent({
  name: 'HomePage',
  setup() {
    const features = [
      { icon: '⚡', title: 'Fine-grained Reactivity', desc: 'Proxy-based reactivity tracks every property access automatically. No manual deps.', api: ['reactive()', 'ref()', 'computed()'] },
      { icon: '🧩', title: 'Component System', desc: 'defineComponent, props, emits, slots, mixins, provide/inject — the full model.', api: ['defineComponent()', 'createApp()'] },
      { icon: '🌳', title: 'Virtual DOM', desc: 'Custom vnode tree with keyed list reconciliation and SVG namespace support.', api: ['h()', 'patch()', 'render()'] },
      { icon: '📜', title: 'Template Compiler', desc: 'Runtime compiler supports v-if, v-for, v-model, v-show, v-bind, v-on, and {{ }}.', api: ['compile()'] },
      { icon: '🛣️', title: 'Official Router', desc: 'Hash & history modes, dynamic params, nested routes, navigation guards.', api: ['createRouter()'] },
      { icon: '🗃️', title: 'State Management', desc: 'Vuex-style store with state, getters, mutations, actions, modules, plugins.', api: ['createStore()'] },
    ]
    return () => h('div', { class: 'fade-in' }, [
      h(Hero),
      h('section', { class: 'section' }, [
        h('div', { class: 'container' }, [
          h('div', { class: 'section__header' }, [
            h('div', { class: 'section__eyebrow' }, 'Features'),
            h('h2', { class: 'section__title' }, 'Everything you would expect from a Vue-class framework'),
            h('p', { class: 'section__desc' }, 'Hues.js ships the full set of Vue.js features — reactivity, components, directives, slots, lifecycle hooks, mixins, SSR, router, store, and CLI.'),
          ]),
          h('div', { class: 'features' },
            features.map((f) =>
              h('div', { class: 'feature', key: f.title }, [
                h('div', { class: 'feature__icon' }, f.icon),
                h('h3', { class: 'feature__title' }, f.title),
                h('p', { class: 'feature__desc' }, f.desc),
                h('div', { class: 'feature__api' }, f.api.map((a) => h('code', { key: a }, a))),
              ]),
            ),
          ),
        ]),
      ]),
    ])
  },
})

const DemosPage = defineComponent({
  name: 'DemosPage',
  setup() {
    return () => h('div', { class: 'fade-in' }, [
      h('section', { class: 'section' }, [
        h('div', { class: 'container' }, [
          h('div', { class: 'section__header' }, [
            h('div', { class: 'section__eyebrow' }, 'Live Demos'),
            h('h2', { class: 'section__title' }, 'Real Hues.js apps running in your browser'),
            h('p', { class: 'section__desc' }, 'This entire website is built with Hues.js. The demos below are real components using the published @deathlegion/hues packages.'),
          ]),
          h('div', { class: 'demo-grid' }, [
            h(DemoCard, {
              title: 'Reactive Counter',
              subtitle: 'ref · computed',
              desc: 'The classic counter — fully driven by Hues.js reactivity.',
            }, [h(CounterDemo)]),
            h(DemoCard, {
              title: 'Todo App + Store',
              subtitle: 'createStore · mutations · getters',
              desc: 'A complete TodoMVC powered by @hues/store.',
            }, [h(TodoDemo)]),
            h(DemoCard, {
              title: 'Live Reactive Chart',
              subtitle: 'SVG · setInterval · computed',
              desc: 'Bar chart that re-renders automatically when data changes.',
            }, [h(ChartDemo)]),
          ]),
        ]),
      ]),
    ])
  },
})

const FeaturesPage = defineComponent({
  name: 'FeaturesPage',
  setup() {
    const features = [
      { icon: '⚡', title: 'Fine-grained Reactivity', desc: 'A from-scratch Proxy-based reactivity system. Every property access is tracked automatically — no manual dependency declarations, no Vue.set-style hacks. Supports nested objects, arrays, Maps and Sets.', api: ['reactive()', 'ref()', 'computed()', 'effect()', 'watch()'] },
      { icon: '🧩', title: 'Component System', desc: 'Functional and Options-API components, props with validation, emits, slots, mixins, provide/inject, expose — the full component model you know from Vue.', api: ['defineComponent()', 'createApp()', 'useSlots()', 'defineExpose()'] },
      { icon: '🌳', title: 'Virtual DOM + Diffing', desc: 'A custom vnode tree with keyed list reconciliation, attribute patching, event listener management, and SVG namespace support.', api: ['h()', 'patch()', 'mount()', 'render()', 'Fragment'] },
      { icon: '📜', title: 'Template Compiler', desc: 'A runtime compiler that turns HTML templates into render functions. Supports v-if, v-for, v-model, v-show, v-bind, v-on with modifiers, and {{ }} interpolation.', api: ['compile()', 'parseTemplate()'] },
      { icon: '♻️', title: 'Lifecycle Hooks', desc: 'The full Vue lifecycle: beforeCreate, created, beforeMount, mounted, beforeUpdate, updated, beforeUnmount, unmounted, errorCaptured, activated, deactivated.', api: ['onMounted()', 'onUpdated()', 'onUnmounted()'] },
      { icon: '🛣️', title: 'Official Router', desc: 'Hash and history modes, dynamic params, nested routes, lazy loading, navigation guards (beforeEach / beforeResolve / afterEach), and per-route beforeEnter.', api: ['createRouter()', 'useRouter()', 'RouterView', 'RouterLink'] },
      { icon: '🗃️', title: 'State Management', desc: 'A Vuex/Pinia-style store with state, getters, mutations, actions, namespaced modules, plugins, strict mode, and the mapState/mapGetters helpers.', api: ['createStore()', 'useStore()', 'mapState()', 'mapActions()'] },
      { icon: '🖥️', title: 'Server-Side Rendering', desc: 'Render any Hues.js component tree to an HTML string on the server. Same component code runs on both sides — only the rendering target changes.', api: ['renderToString()', 'createSSRApp()', 'hydrate()'] },
      { icon: '⌨️', title: 'CLI Scaffolding', desc: 'A full project CLI with create / dev / build / generate / inspect / add / info commands and templates for default, SSR, library, PWA, TypeScript, and TSX.', api: ['hues create', 'hues dev', 'hues build'] },
      { icon: '🔌', title: 'Plugin System', desc: 'Install plugins at the app level with app.use(). Plugins can register components, directives, mixins, provide/inject, and global properties.', api: ['app.use()', 'app.provide()', 'app.component()'] },
      { icon: '🎯', title: 'TypeScript Native', desc: 'The entire codebase is written in TypeScript with strict types. Props are validated, emits are typed, and the public API surface is fully documented.', api: ['strict: true', 'interface PropDef'] },
      { icon: '🌐', title: 'ES5 Browser Compatible', desc: 'Compiled output targets ES5 with the DOM lib. Hues.js runs in every modern browser — including the long tail where Vue.js itself ships.', api: ['target: ES5', 'lib: ["ES5", "DOM"]'] },
    ]
    return () => h('div', { class: 'fade-in' }, [
      h('section', { class: 'section' }, [
        h('div', { class: 'container' }, [
          h('div', { class: 'section__header' }, [
            h('div', { class: 'section__eyebrow' }, 'Features'),
            h('h2', { class: 'section__title' }, 'The full Hues.js feature surface'),
            h('p', { class: 'section__desc' }, 'No MVP, no shortcuts. The complete Vue.js-class feature set, built from scratch in TypeScript.'),
          ]),
          h('div', { class: 'features' },
            features.map((f) =>
              h('div', { class: 'feature', key: f.title }, [
                h('div', { class: 'feature__icon' }, f.icon),
                h('h3', { class: 'feature__title' }, f.title),
                h('p', { class: 'feature__desc' }, f.desc),
                h('div', { class: 'feature__api' }, f.api.map((a) => h('code', { key: a }, a))),
              ]),
            ),
          ),
        ]),
      ]),
    ])
  },
})

const InstallPage = defineComponent({
  name: 'InstallPage',
  setup() {
    const copied = ref<string>('')
    const copy = (text: string, label: string) => {
      try {
        navigator.clipboard.writeText(text)
        copied.value = label
        setTimeout(() => { copied.value = '' }, 1500)
      } catch { /* noop */ }
    }
    const steps = [
      {
        title: 'Install the package',
        desc: 'Hues.js is published on npm under the @deathlegion scope. Install the full ecosystem in one command.',
        cmd: 'npm install @deathlegion/hues',
        label: 'install',
      },
      {
        title: 'Or install individual packages',
        desc: 'Prefer to start small? Each package is independent and tree-shakable.',
        cmd: 'npm install @deathlegion/hues-core @deathlegion/hues-router @deathlegion/hues-store',
        label: 'individual',
      },
      {
        title: 'Create your first app',
        desc: 'Import the core primitives and mount a Hues.js app to any DOM element.',
        cmd: `import { createApp, ref, h } from '@deathlegion/hues'

const app = createApp({
  setup() {
    const count = ref(0)
    return () => h('div', null, [
      h('h1', null, 'Hello Hues.js!'),
      h('p', null, 'Count: ' + count.value),
      h('button', { onClick: () => count.value++ }, 'Increment'),
    ])
  }
})

app.mount('#app')`,
        label: 'app',
      },
      {
        title: 'Add the router and store',
        desc: 'Install the official ecosystem packages and plug them in via app.use().',
        cmd: `import { createRouter } from '@deathlegion/hues-router'
import { createStore } from '@deathlegion/hues-store'

const router = createRouter({
  history: 'hash',
  routes: [
    { path: '/', component: Home },
    { path: '/about', component: About },
  ],
})

const store = createStore({
  state: () => ({ count: 0 }),
  mutations: { increment: (s) => s.count++ },
})

app.use(router)
app.use(store)`,
        label: 'router-store',
      },
    ]
    const cdnSnippet = `<!-- 1. Include Hues.js from the CDN (free, no build step) -->
<script src="https://unpkg.com/@deathlegion/hues"></script>

<!-- 2. Use it — exposes window.Hues -->
<div id="app"></div>
<script>
  const { createApp, ref, h } = Hues
  createApp({
    setup() {
      const count = ref(0)
      return () => h('div', null, [
        h('h1', null, 'Count: ' + count.value),
        h('button', { onClick: () => count.value++ }, '+'),
      ])
    }
  }).mount('#app')
</script>`

    const cdnUrls = [
      { label: 'unpkg', url: 'https://unpkg.com/@deathlegion/hues' },
      { label: 'jsDelivr', url: 'https://cdn.jsdelivr.net/npm/@deathlegion/hues' },
      { label: 'Cloudflare', url: 'https://hues-js.pages.dev/hues.min.js' },
    ]

    return () => h('div', { class: 'fade-in' }, [
      // CDN Quick Start section
      h('section', { class: 'section' }, [
        h('div', { class: 'container' }, [
          h('div', { class: 'section__header' }, [
            h('div', { class: 'section__eyebrow' }, 'Quick Start · Free CDN'),
            h('h2', { class: 'section__title' }, 'No build step. No npm install. Just one script tag.'),
            h('p', { class: 'section__desc' }, 'Hues.js is available on three free CDNs. Drop a single <script> tag into any HTML file and you have a full reactive framework — reactivity, components, router, store, SSR. 30KB minified, runs in every ES5 browser.'),
          ]),
          h('div', { style: { display: 'flex', gap: '0.5rem', justifyContent: 'center', marginBottom: '1.5rem', flexWrap: 'wrap' } },
            cdnUrls.map((cdn) =>
              h('div', { key: cdn.label, style: { display: 'flex', flexDirection: 'column', gap: '0.25rem', alignItems: 'center' } }, [
                h('span', { style: { fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--green)' } }, cdn.label),
                h('code', { style: { fontSize: '0.6875rem', fontFamily: 'ui-monospace, monospace', color: 'var(--text-muted)', background: 'var(--surface)', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', border: '1px solid var(--border)' } }, cdn.url),
              ]),
            ),
          ),
          h('div', { class: 'code-block', style: { maxWidth: '800px', margin: '0 auto' } }, [
            h('div', { class: 'code-block__header' }, [
              h('div', { class: 'code-block__dots' }, [h('span'), h('span'), h('span')]),
              h('span', { class: 'code-block__filename' }, 'index.html'),
              h('button', {
                class: 'code-block__copy',
                onClick: () => copy(cdnSnippet, 'cdn'),
              }, copied.value === 'cdn' ? 'Copied ✓' : 'Copy'),
            ]),
            h('pre', null, cdnSnippet),
          ]),
          h('p', { style: { textAlign: 'center', marginTop: '1rem', fontSize: '0.8125rem', color: 'var(--text-muted)' } }, [
            'That\'s it. Open the HTML file in a browser — no Node.js, no bundler, no npm. ',
            h('strong', { style: { color: 'var(--text)' } }, 'Fully free, forever.'),
          ]),
        ]),
      ]),
      // npm install steps
      h('section', { class: 'section section--alt' }, [
        h('div', { class: 'container' }, [
          h('div', { class: 'section__header' }, [
            h('div', { class: 'section__eyebrow' }, 'npm / yarn / pnpm'),
            h('h2', { class: 'section__title' }, 'Or install via package manager'),
            h('p', { class: 'section__desc' }, 'For build-tool workflows, install from npm. Tree-shakable, typed, and works with any bundler.'),
          ]),
          h('div', { class: 'install-grid' },
            steps.map((step, idx) =>
              h('div', { class: 'install-step', key: step.label }, [
                h('div', { class: 'install-step__num' }, String(idx + 1)),
                h('div', { class: 'install-step__body' }, [
                  h('h3', { class: 'install-step__title' }, step.title),
                  h('p', { class: 'install-step__desc' }, step.desc),
                  h('div', { class: 'code-block' }, [
                    h('div', { class: 'code-block__header' }, [
                      h('div', { class: 'code-block__dots' }, [
                        h('span'), h('span'), h('span'),
                      ]),
                      h('button', {
                        class: 'code-block__copy',
                        onClick: () => copy(step.cmd, step.label),
                      }, copied.value === step.label ? 'Copied ✓' : 'Copy'),
                    ]),
                    h('pre', null, step.cmd),
                  ]),
                ]),
              ]),
            ),
          ),
        ]),
      ]),
    ])
  },
})

const DocsPage = defineComponent({
  name: 'DocsPage',
  setup() {
    const copied = ref('')
    const copy = (text: string) => {
      try {
        navigator.clipboard.writeText(text)
        copied.value = 'copied'
        setTimeout(() => { copied.value = '' }, 1500)
      } catch { /* noop */ }
    }
    const sections = [
      {
        title: 'Reactivity',
        code: `import { ref, computed, watch, effect } from '@deathlegion/hues'

const count = ref(0)
const doubled = computed(() => count.value * 2)

effect(() => {
  console.log('count is now', count.value)
})

watch(count, (newVal, oldVal) => {
  console.log('count changed from', oldVal, 'to', newVal)
})

count.value++    // → 1, doubled === 2
count.value = 10 // → 10, doubled === 20`,
      },
      {
        title: 'Components',
        code: `import { defineComponent, h, ref } from '@deathlegion/hues'

export const Counter = defineComponent({
  name: 'Counter',
  props: { initial: { type: Number, default: 0 } },
  emits: ['change'],
  setup(props, { emit }) {
    const count = ref(props.initial)
    return () => h('div', null, [
      h('span', null, String(count.value)),
      h('button', {
        onClick: () => { count.value++; emit('change', count.value) },
      }, '+'),
    ])
  },
})`,
      },
      {
        title: 'Router',
        code: `import { createRouter } from '@deathlegion/hues-router'

export const router = createRouter({
  history: 'hash',
  routes: [
    { path: '/', name: 'home', component: Home },
    { path: '/users/:id', name: 'user', component: User, props: true },
    {
      path: '/admin',
      component: Admin,
      beforeEnter: (to, from, next) => {
        if (!isAuthenticated()) next('/login')
        else next()
      },
    },
  ],
})

router.beforeEach((to, from, next) => {
  console.log(\`Navigating: \${from.path} → \${to.path}\`)
  next()
})`,
      },
      {
        title: 'Store',
        code: `import { createStore } from '@deathlegion/hues-store'

export const store = createStore({
  state: () => ({
    user: null,
    cart: [],
  }),
  getters: {
    isLoggedIn: (s) => !!s.user,
    cartTotal: (s) => s.cart.reduce((sum, i) => sum + i.price, 0),
  },
  mutations: {
    setUser(s, user) { s.user = user },
    addToCart(s, item) { s.cart.push(item) },
  },
  actions: {
    async login({ commit }, credentials) {
      const user = await api.login(credentials)
      commit('setUser', user)
    },
  },
})`,
      },
    ]
    return () => h('div', { class: 'fade-in' }, [
      h('section', { class: 'section' }, [
        h('div', { class: 'container' }, [
          h('div', { class: 'section__header' }, [
            h('div', { class: 'section__eyebrow' }, 'Documentation'),
            h('h2', { class: 'section__title' }, 'Code examples'),
            h('p', { class: 'section__desc' }, 'Copy-pasteable Hues.js code covering the core APIs.'),
          ]),
          h('div', { style: { display: 'grid', gap: '1.5rem', maxWidth: '900px', margin: '0 auto' } },
            sections.map((s) =>
              h('div', { key: s.title }, [
                h('h3', { style: { fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.75rem' } }, s.title),
                h('div', { class: 'code-block' }, [
                  h('div', { class: 'code-block__header' }, [
                    h('div', { class: 'code-block__dots' }, [h('span'), h('span'), h('span')]),
                    h('button', {
                      class: 'code-block__copy',
                      onClick: () => copy(s.code),
                    }, copied.value === 'copied' ? 'Copied ✓' : 'Copy'),
                  ]),
                  h('pre', null, s.code),
                ]),
              ]),
            ),
          ),
        ]),
      ]),
    ])
  },
})

// ───────────────────────── Footer ─────────────────────────

const Footer = defineComponent({
  name: 'SiteFooter',
  setup() {
    return () => h('footer', { class: 'footer' }, [
      h('div', { class: 'footer__inner' }, [
        h('div', null, [
          h('div', { class: 'footer__brand' }, [
            h(Logo, { size: 28 }),
            h('div', null, [
              h('div', { class: 'brand__name', style: { fontSize: '0.875rem' } }, ['Hues', h('span', null, '.js')]),
              h('div', { class: 'brand__tag' }, 'by Death Legion'),
            ]),
          ]),
          h('p', { class: 'footer__desc' }, 'A progressive JavaScript framework for building user interfaces. Built from scratch in TypeScript — reactivity, virtual DOM, components, router, store, SSR, and CLI.'),
        ]),
        h('div', { class: 'footer__col' }, [
          h('h4', null, 'Packages'),
          h('ul', null, [
            h('li', null, h('a', { href: 'https://www.npmjs.com/package/@deathlegion/hues', target: '_blank', rel: 'noreferrer' }, '@deathlegion/hues')),
            h('li', null, h('a', { href: 'https://www.npmjs.com/package/@deathlegion/hues-core', target: '_blank', rel: 'noreferrer' }, '@deathlegion/hues-core')),
            h('li', null, h('a', { href: 'https://www.npmjs.com/package/@deathlegion/hues-router', target: '_blank', rel: 'noreferrer' }, '@deathlegion/hues-router')),
            h('li', null, h('a', { href: 'https://www.npmjs.com/package/@deathlegion/hues-store', target: '_blank', rel: 'noreferrer' }, '@deathlegion/hues-store')),
            h('li', null, h('a', { href: 'https://www.npmjs.com/package/@deathlegion/hues-server', target: '_blank', rel: 'noreferrer' }, '@deathlegion/hues-server')),
            h('li', null, h('a', { href: 'https://www.npmjs.com/package/@deathlegion/hues-cli', target: '_blank', rel: 'noreferrer' }, '@deathlegion/hues-cli')),
          ]),
        ]),
        h('div', { class: 'footer__col' }, [
          h('h4', null, 'Resources'),
          h('ul', null, [
            h('li', null, h('a', { href: '#/demos' }, 'Live Demos')),
            h('li', null, h('a', { href: '#/features' }, 'Features')),
            h('li', null, h('a', { href: '#/install' }, 'Installation')),
            h('li', null, h('a', { href: '#/docs' }, 'Documentation')),
            h('li', null, h('a', { href: 'https://www.npmjs.com/package/dulice-icons', target: '_blank', rel: 'noreferrer' }, 'Dulice Icons')),
          ]),
        ]),
      ]),
      h('div', { class: 'footer__bottom' }, [
        h('span', null, `© ${new Date().getFullYear()} Hues.js by Death Legion · UNLICENSED · v${HUES_VERSION}`),
        h('span', null, 'Built with Hues.js itself'),
      ]),
    ])
  },
})

// ───────────────────────── Root App ─────────────────────────

const App = defineComponent({
  name: 'App',
  setup() {
    return () => h('div', null, [
      h(Header),
      h('main', null, [h(RouterView)]),
      h(Footer),
    ])
  },
})

// ───────────────────────── Router + Mount ─────────────────────────

const router = createRouter({
  history: 'hash',
  routes: [
    { path: '/', name: 'home', component: HomePage },
    { path: '/demos', name: 'demos', component: DemosPage },
    { path: '/features', name: 'features', component: FeaturesPage },
    { path: '/install', name: 'install', component: InstallPage },
    { path: '/docs', name: 'docs', component: DocsPage },
  ],
})

const app = createApp(App)
app.use(router)
app.use(todoStore)
app.mount('#app')

// Initialize route
if (typeof window !== 'undefined' && !window.location.hash) {
  window.location.hash = '#/'
}
